import moment from "moment";
import type Stripe from "stripe";

import { IStripeSubscriptionExtended } from "common/interfaces";
import { guessUserCountry } from "js/react/views/Billing/Checkout/utils/countries";

export const Customer = {
    getTaxId: (customer: Stripe.Customer) => {
        return customer?.tax_ids?.data?.[0]?.value ?? "";
    },
    getTaxIdType: (customer: Stripe.Customer) => {
        return customer?.tax_ids?.data?.[0]?.type ?? null;
    },
    getBillingAddress: (customer: Stripe.Customer, paymentMethod: Stripe.PaymentMethod) => {
        return customer?.address ??
            paymentMethod?.billing_details.address ??
            {
                city: "",
                country: guessUserCountry(),
                line1: "",
                line2: "",
                postal_code: "",
                state: ""
            };
    }
};

export const Subscription = {
    getTrialDaysRemaining: (subscription: IStripeSubscriptionExtended) => {
        try {
            const isInTrial = subscription?.status === "trialing";
            return isInTrial ? Math.ceil(
                (subscription?.trial_end * 1000 - new Date().getTime()) / 1000 / 60 / 60 / 24,
            ) : 0;
        } catch {
            return 0;
        }
    },
    getProrationCredit: (subscription: IStripeSubscriptionExtended, {
        newPlanAmountDollars,
        newSeats,
    }) => {
        const currentItem = subscription?.items?.data?.[0];
        const currentInterval = currentItem?.price?.recurring?.interval;
        const currentSeats = currentItem?.quantity ?? 0;
        const currentUnitAmountCents = currentItem?.price?.unit_amount ?? 0;
        const currentPeriodEnd = subscription?.current_period_end;
        const currentPeriodStart = subscription?.current_period_start;
        const trialDaysRemaining = Subscription.getTrialDaysRemaining(subscription);

        if (trialDaysRemaining > 0) {
            // if trial no proration
            return 0;
        } else if (!currentItem || !currentPeriodEnd || !currentPeriodStart || !currentInterval) {
            return 0;
        } else {
            //proration logic
            const now = moment();
            const periodEnd = moment.unix(currentPeriodEnd);
            const periodStart = moment.unix(currentPeriodStart);

            const totalMsInPeriod = periodEnd.valueOf() - periodStart.valueOf();
            const remainingMsInPeriod = periodEnd.valueOf() - now.valueOf();

            // Check for invalid states (e.g., expired period)
            if (totalMsInPeriod <= 0 || remainingMsInPeriod <= 0) {
                // Cannot prorate, charge full amount
                return 0;
            } else {
                // Calculate prorated credit and cost
                const fractionRemaining = remainingMsInPeriod / totalMsInPeriod;

                // Credit for unused time on current plan
                const currentTotalCostDollars = (currentUnitAmountCents * currentSeats) / 100;
                const creditForCurrentPlan = currentTotalCostDollars * fractionRemaining;
                const newTotalCostDollars = newPlanAmountDollars * newSeats;
                const costForUpgradedPlan = newTotalCostDollars * fractionRemaining;
                return newTotalCostDollars - costForUpgradedPlan + creditForCurrentPlan;
            }
        }
    }
};
